import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { CoverBlock } from "../../api/types";

interface CoverSlideViewProps {
  block: CoverBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  /** When true (print/presentation), fill the page container instead of a
   *  fixed minimum height. */
  fill?: boolean;
}

/**
 * Branded title / section-divider slide. Big centred title + subtitle +
 * period label over a dark background with a brand accent line. No data fetch.
 */
export const CoverSlideView: React.FC<CoverSlideViewProps> = ({
  block,
  branding,
  fill = false,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        bgcolor: "#0f1419",
        color: "#fff",
        borderRadius: 2,
        px: 6,
        py: fill ? 8 : 6,
        minHeight: fill ? "100%" : 360,
        width: "100%",
      }}
    >
      <Typography
        variant="h3"
        sx={{ fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em" }}
      >
        {block.title}
      </Typography>

      <Box
        sx={{
          width: 96,
          height: 4,
          borderRadius: 2,
          bgcolor: accent,
          my: 3,
        }}
      />

      {block.subtitle && (
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, color: "rgba(255,255,255,0.82)", maxWidth: 720 }}
        >
          {block.subtitle}
        </Typography>
      )}

      {block.periodLabel && (
        <Typography
          variant="subtitle1"
          sx={{
            mt: 2,
            color: accent,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {block.periodLabel}
        </Typography>
      )}
    </Box>
  );
};
